import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import API from '../services/api';

const statusVariant = (s = '') => {
  const v = String(s || '').toLowerCase();
  if (v === 'success') return 'success';
  if (v === 'failed' || v === 'error' || v === 'cancelled') return 'danger';
  if (v === 'pending' || v === 'created') return 'warning';
  return 'secondary';
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN');
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '₹ 0';
  return `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const PhonePeTransactionsPage = ({ endpoint, title = 'PhonePe Transactions' }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (nextPage = page) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await API.get(endpoint, {
        params: {
          page: nextPage,
          limit: 20,
          status: status || undefined,
          search: search || undefined,
        },
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setSummary(data?.summary || {});
      setPage(Number(data?.page || 1));
      setPages(Number(data?.pages || 1));
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const summaryItems = useMemo(
    () => [
      { key: 'success', label: 'Success' },
      { key: 'pending', label: 'Pending' },
      { key: 'failed', label: 'Failed' },
      { key: 'created', label: 'Created' },
    ],
    []
  );

  const applyFilters = () => load(1);

  return (
    <div className="container-fluid py-2">
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="fw-semibold d-flex align-items-center gap-2">
            <span className="material-symbols-outlined">payments</span>
            {title}
          </div>
          <Badge bg="primary">Total: {total}</Badge>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <div className="d-flex flex-wrap gap-2 mb-3">
            {summaryItems.map((x) => (
              <Badge key={x.key} bg={statusVariant(x.key)} className="px-2 py-2">
                {x.label}: {Number(summary?.[x.key] || 0)}
              </Badge>
            ))}
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All status</option>
                <option value="created">Created</option>
                <option value="pending">Pending</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
                <option value="error">Error</option>
              </Form.Select>
            </div>
            <div className="col-md-7">
              <Form.Control
                placeholder="Search txn id / purpose"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-2 d-grid">
              <Button onClick={applyFilters}>Apply</Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4 text-muted"><Spinner size="sm" className="me-2" />Loading...</div>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Purpose</th>
                    <th>User</th>
                    <th>Merchant Txn ID</th>
                    <th>PhonePe Txn ID</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((r) => (
                      <tr key={r._id}>
                        <td>{fmtDate(r.createdAt)}</td>
                        <td><Badge bg={statusVariant(r.status)}>{r.status}</Badge></td>
                        <td>{fmtMoney(r.amountRupees)}</td>
                        <td>{r.purpose || '—'}</td>
                        <td>{r.userId?.fullName || r.userId?.mobile || '—'}</td>
                        <td style={{ fontFamily: 'monospace' }}>{r.merchantTransactionId || '—'}</td>
                        <td style={{ fontFamily: 'monospace' }}>{r.phonepeTransactionId || '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">No transactions found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">Page {page} of {pages}</small>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => load(page - 1)}>Prev</Button>
              <Button variant="outline-secondary" size="sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next</Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PhonePeTransactionsPage;
